// src/pages/About.jsx
// ────────────────────────────────────────────────────────────────
// Versión actualizada: nuevos Valores, Misión, Visión & Objetivos
// Paleta KW Solar  ·  Tipografía Ethos Nova  ·  100 % responsive
// ────────────────────────────────────────────────────────────────
import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { FiCheckCircle } from "react-icons/fi";
import ScrollFade from "../components/ScrollFade";

/* ---- Assets ---- */
import heroImg from "../assets/solar-farm.jpg";
import historiaImg from "../assets/solar-installation.jpg";
import valCliente from "../assets/eco-home.jpg";
import valSustentabilidad from "../assets/solar-farm.jpg";
import valCalidad from "../assets/solar-installation.jpg";
import valInnovacion from "../assets/solar-roof.jpg";
import valResponsabilidad from "../assets/solar-team.jpg";

/* ---- Datos ---- */
const VALUES = [
  {
    img: valCliente,
    title: "Compromiso con el cliente",
    text: "Cada proyecto es una oportunidad para ofrecer soluciones claras, honestas y efectivas.",
    color: "#E06A00",
  },
  {
    img: valSustentabilidad,
    title: "Sustentabilidad",
    text: "Trabajamos por un México más limpio, promoviendo la energía solar como base de un desarrollo responsable.",
    color: "#ffbc5e",
  },
  {
    img: valCalidad,
    title: "Calidad y excelencia",
    text: "Utilizamos equipos certificados y garantizados para asegurar máximo rendimiento y durabilidad.",
    color: "#41B883",
  },
  {
    img: valInnovacion,
    title: "Innovación",
    text: "Implementamos nuevas tecnologías y procesos eficientes para ofrecer lo mejor a nuestros clientes.",
    color: "#3B82F6",
  },
  {
    img: valResponsabilidad,
    title: "Responsabilidad social",
    text: "Creemos en la energía como herramienta para transformar positivamente a las familias y empresas de México.",
    color: "#f5f5f5",
  },
];

const ACCENT = "#E06A00";

/* ---- Animaciones ---- */
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (d = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: d, duration: 0.6, ease: "easeOut" },
  }),
};

export default function About() {
  const theme = useTheme();

  return (
    <Box component="main">
      {/* HERO full-height */}
      <Box
        sx={{
          position: "relative",
          height: "100dvh",
          background: `linear-gradient(rgba(0,0,0,.55),rgba(0,0,0,.55)),url(${heroImg}) center/cover`,
          display: "flex",
          alignItems: "center",
          color: "#fff",
        }}
      >
        <Container>
          <Typography
            component={motion.h1}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            fontSize={{ xs: 32, md: 56 }}
            fontWeight={900}
            gutterBottom
          >
            Energía&nbsp;con causa
          </Typography>
          <Typography
            component={motion.p}
            variants={fadeUp}
            animate="visible"
            custom={0.1}
            maxWidth={640}
            mb={4}
          >
            Creemos que el sol es una fuente ilimitada que nos permite enfocar
            la energía donde&nbsp;<b>más importa</b>.
          </Typography>
          <Button
            component={NavLink}
            to="/contacto"
            variant="contained"
            sx={{
              bgcolor: ACCENT,
              fontWeight: 700,
              textTransform: "none",
              py: 1.4,
              px: 5,
              borderRadius: 3,
              "&:hover": { bgcolor: "#d65d00" },
            }}
          >
            Conoce más
          </Button>
        </Container>
      </Box>
      {/* HISTORIA */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container
          maxWidth="lg"
          sx={{ display: "flex", gap: 6, flexWrap: "wrap" }}
        >
          <Box flex={{ xs: "1 1 420px", md: "1 1 50%" }}>
            <ScrollFade variants={fadeUp}>
              <Box
                component="img"
                src={historiaImg}
                alt="Historia KW Solar"
                sx={{ width: "100%", borderRadius: 3, boxShadow: 3 }}
              />
            </ScrollFade>
          </Box>
          <Box
            flex={{ xs: "1 1 320px", md: "1 1 45%" }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <ScrollFade variants={fadeUp} custom={0.1}>
              <Typography variant="h4" fontWeight={800} gutterBottom>
                Nuestra historia
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                KW Solar Energy nació en 2023 con la misión de acercar la
                energía solar a hogares y empresas de todo México. Aun siendo
                jóvenes, hemos impulsado más de 100 proyectos fotovoltaicos,
                reduciendo costos y emisiones para nuestros clientes.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Creemos en la transición energética como motor de cambio
                económico y ambiental, y trabajamos para convertir ese futuro
                limpio en realidad.
              </Typography>
            </ScrollFade>
          </Box>
        </Container>
      </Box>
      {/* MISIÓN & VISIÓN */}
      <Box sx={{ bgcolor: theme.palette.grey[900], py: { xs: 8, md: 10 } }}>
        <Container
          maxWidth="lg"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            color: "#fff",
          }}
        >
          {[
            {
              title: "Misión",
              bg: ACCENT,
              text: "Acercar la energía solar a familias y empresas en todo México, con soluciones personalizadas, accesibles y de alta calidad, contribuyendo al ahorro económico y al cuidado del medio ambiente.",
            },
            {
              title: "Visión",
              bg: theme.palette.grey[800],
              text: "Ser la empresa líder en soluciones solares residenciales y comerciales en México, reconocida por su calidad, profesionalismo e impacto positivo en la transición energética del país.",
            },
          ].map((b, i) => (
            <ScrollFade key={b.title} variants={fadeUp} custom={i * 0.1}>
              <Box
                sx={{
                  flex: 1,
                  p: { xs: 4, md: 6 },
                  background: b.bg,
                  borderRadius: 2,
                  minHeight: 180,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h5" fontWeight={800} gutterBottom>
                  {b.title}
                </Typography>
                <Typography variant="body1">{b.text}</Typography>
              </Box>
            </ScrollFade>
          ))}
        </Container>
      </Box>
      {/* OBJETIVOS */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#FFF7F0" }}>
        <Container maxWidth="md">
          <ScrollFade variants={fadeUp}>
            <Typography variant="h4" fontWeight={800} align="center" mb={4}>
              Nuestros objetivos
            </Typography>
          </ScrollFade>

          <ScrollFade variants={fadeUp} custom={0.1}>
            <List dense>
              {[
                "Ayudar a más de 1 000 hogares y negocios a migrar a energía solar en los próximos 3 años.",
                "Reducir colectivamente más de 2 000 toneladas de CO₂ anuales con nuestros sistemas instalados.",
                "Construir alianzas estratégicas con proveedores y financieras para ofrecer las mejores condiciones del mercado.",
                "Consolidar un equipo técnico de primer nivel en las principales ciudades del país.",
                "Posicionar a KW Solar Energy como referente nacional en soluciones solares confiables y accesibles.",
              ].map((obj) => (
                <ListItem key={obj} sx={{ alignItems: "flex-start" }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <FiCheckCircle color={ACCENT} />
                  </ListItemIcon>
                  <ListItemText primary={obj} />
                </ListItem>
              ))}
            </List>
          </ScrollFade>
        </Container>
      </Box>
      <Box sx={{ bgcolor: "#fff", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg" disableGutters>
          <ScrollFade variants={fadeUp}>
            <Typography variant="h4" fontWeight={800} align="center" mb={6}>
              Nuestros valores
            </Typography>
          </ScrollFade>

          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            sx={{
              display: "flex",
              gap: 3, // 24 px
              overflowX: { xs: "auto", md: "hidden" }, // scroll solo en móvil
              scrollSnapType: { xs: "x mandatory", md: "none" },
              pb: { xs: 2, md: 0 },
              px: { xs: 2, md: 0 }, // pequeño padding en móvil
            }}
          >
            {VALUES.map((v, i) => (
              <Box
                key={v.title}
                component={motion.div}
                whileHover={{ translateY: -6 }}
                sx={{
                  /*  ➜ 280 px en móvil, anchura calculada exacta en desktop */
                  flex: {
                    xs: "0 0 280px",
                    md: "0 0 calc((100% - 96px) / 5)",
                  },
                  maxWidth: {
                    xs: 280,
                    md: "calc((100% - 96px) / 5)",
                  },
                  scrollSnapAlign: "start",
                  height: "100%",
                  minHeight: 260,
                  borderRadius: 4,
                  overflow: "hidden",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  boxShadow: "0 8px 30px rgba(0,0,0,.12)",
                  background: `url(${v.img}) center/cover`,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    backdropFilter: "blur(4px)",
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,.55) 95%)",
                  },
                }}
              >
                <Box
                  sx={{ position: "relative", zIndex: 1, color: "#fff", p: 4 }}
                >
                  <Typography
                    variant="h2"
                    fontWeight={900}
                    sx={{ opacity: 0.35, lineHeight: 0.8 }}
                  >
                    {`0${i + 1}`}
                  </Typography>
                  <Typography variant="h6" fontWeight={800} gutterBottom>
                    {v.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {v.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
